import React from 'react';

const VND = n =>
  new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(n ?? 0);

const fmtMonth = s => {
  if (!s) return '—';
  const [y, m] = s.split('-');
  return `Tháng ${m}/${y}`;
};

const STATUS_LABEL = {
  pending:   { label: 'Chờ thanh toán', color: '#f59e0b' },
  partial:   { label: 'Thanh toán một phần', color: '#3b82f6' },
  confirmed: { label: 'Đã xác nhận', color: '#10b981' },
  completed: { label: 'Hoàn tất', color: '#10b981' },
  overdue:   { label: 'Quá hạn', color: '#ef4444' },
  rejected:  { label: 'Từ chối', color: '#6b7280' },
  refunded:  { label: 'Đã hoàn tiền', color: '#8b5cf6' },
};

/**
 * A standalone receipt card rendered off-screen, captured by html-to-image.
 * Keep all styles inline — Tailwind classes are purged and unreliable for off-screen nodes.
 */
export default function BillExportCard({ payment, meterReadings = [] }) {
  if (!payment) return null;

  // Parse services detail
  let detail = payment.other_costs_detail;
  if (typeof detail === 'string') { try { detail = JSON.parse(detail); } catch { detail = {}; } }
  detail = detail || {};

  const meteredKeys = new Set(['electricity', 'electric', 'water']);
  const fixedServices = Object.entries(detail).filter(
    ([k]) => !meteredKeys.has(k) && !['first_month_discount', 'commission_amount'].includes(k)
  );
  const discounts = Object.entries(detail).filter(([k]) =>
    ['first_month_discount', 'commission_amount'].includes(k)
  );

  // Surcharges
  let surcharges = payment.surcharges || [];
  if (typeof surcharges === 'string') { try { surcharges = JSON.parse(surcharges); } catch { surcharges = []; } }
  const surchargesSum = surcharges.reduce((s, x) => s + (x.total || x.amount || 0), 0);

  // Verified meter readings
  const verifiedMeters = meterReadings.filter(m => m.status === 'verified' && !m.is_base_reading);
  const elec = verifiedMeters.filter(m => ['electricity', 'electric'].includes(m.reading_type));
  const water = verifiedMeters.filter(m => m.reading_type === 'water');

  // Proration from admin_note
  const note = payment.admin_note || '';
  const prorateMatch = note.match(/tỷ lệ (\d+)\/(\d+) ngày/);
  const moveoutMatch = note.match(/Ngày rời: (\d{4}-\d{2}-\d{2})/);
  const isProrated = !!prorateMatch;

  // Outstanding
  const totalInvoice = (payment.invoice_amount ?? 0) + surchargesSum;
  const outstanding = Math.max(0, totalInvoice - (payment.paid_amount ?? 0));

  const statusInfo = STATUS_LABEL[payment.status] || { label: payment.status, color: '#6b7280' };
  const generatedAt = new Date().toLocaleString('vi-VN', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });

  const s = {
    wrap: {
      width: 420, background: '#fff', fontFamily: '"Inter", "Segoe UI", sans-serif',
      color: '#1c1917', padding: '28px 28px 24px',
    },
    header: {
      borderBottom: '2px solid #0d9488', paddingBottom: 14, marginBottom: 16,
    },
    title: {
      fontSize: 18, fontWeight: 700, color: '#0d9488', letterSpacing: 1, textTransform: 'uppercase',
      marginBottom: 4,
    },
    subtitle: { fontSize: 12, color: '#78716c' },
    metaGrid: {
      display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 12px',
      marginBottom: 16, fontSize: 12,
    },
    metaLabel: { color: '#78716c', marginBottom: 1 },
    metaValue: { fontWeight: 600, color: '#1c1917' },
    sectionTitle: {
      fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em',
      color: '#a8a29e', marginBottom: 6, marginTop: 14,
    },
    row: {
      display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
      paddingTop: 5, paddingBottom: 5, borderBottom: '1px solid #f5f5f4', fontSize: 12,
    },
    rowLabel: { color: '#44403c', flex: 1 },
    rowSub: { fontSize: 10, color: '#a8a29e', marginTop: 1 },
    rowAmt: { fontWeight: 600, color: '#1c1917', marginLeft: 8, whiteSpace: 'nowrap' },
    divider: { height: 1, background: '#e7e5e4', margin: '12px 0' },
    totalRow: {
      display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
      padding: '8px 0', fontSize: 15, fontWeight: 700,
    },
    badge: {
      display: 'inline-block', padding: '2px 8px', borderRadius: 99,
      fontSize: 10, fontWeight: 700, color: '#fff', background: statusInfo.color,
      marginLeft: 8,
    },
    prorateBox: {
      background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 6,
      padding: '6px 10px', fontSize: 11, color: '#0369a1', marginTop: 10,
    },
    footer: {
      marginTop: 18, paddingTop: 10, borderTop: '1px solid #f5f5f4',
      fontSize: 10, color: '#a8a29e', textAlign: 'center',
    },
  };

  return (
    <div style={s.wrap}>
      {/* Header */}
      <div style={s.header}>
        <div style={s.title}>Hóa đơn tiền phòng</div>
        <div style={s.subtitle}>
          {fmtMonth(payment.month)} · Phòng {payment.room_number || '—'}
        </div>
      </div>

      {/* Meta info */}
      <div style={s.metaGrid}>
        <div>
          <div style={s.metaLabel}>Người thuê</div>
          <div style={s.metaValue}>{payment.renter_name || '—'}</div>
        </div>
        <div>
          <div style={s.metaLabel}>Phòng</div>
          <div style={s.metaValue}>{payment.room_number || '—'}</div>
        </div>
        <div>
          <div style={s.metaLabel}>Kỳ thanh toán</div>
          <div style={s.metaValue}>{fmtMonth(payment.month)}</div>
        </div>
        <div>
          <div style={s.metaLabel}>Trạng thái</div>
          <div style={{ ...s.metaValue, color: statusInfo.color }}>{statusInfo.label}</div>
        </div>
      </div>

      <div style={s.divider} />

      {/* Line items */}
      <div style={s.sectionTitle}>Chi tiết hóa đơn</div>

      {/* Base rent */}
      <div style={s.row}>
        <div style={s.rowLabel}>
          Tiền thuê cơ bản
          {isProrated && (
            <div style={s.rowSub}>
              Tỷ lệ {prorateMatch[1]}/{prorateMatch[2]} ngày
              {moveoutMatch ? ` · rời ${moveoutMatch[1].split('-').reverse().join('/')}` : ''}
            </div>
          )}
        </div>
        <div style={s.rowAmt}>{VND(payment.base_rent ?? 0)}</div>
      </div>

      {/* Fixed services */}
      {fixedServices.length > 0 && (
        <>
          <div style={{ ...s.sectionTitle, marginTop: 10 }}>Dịch vụ cố định</div>
          {fixedServices.map(([key, svc]) => (
            <div key={key} style={s.row}>
              <div style={s.rowLabel}>
                {svc?.name || key}
                {svc?.multiplier > 1 && (
                  <span style={{ color: '#a8a29e', marginLeft: 4, fontSize: 10 }}>
                    × {svc.multiplier} {svc.unit === 'person' ? 'người' : (svc.unit || '')}
                  </span>
                )}
              </div>
              <div style={s.rowAmt}>{VND(svc?.price ?? svc?.amount ?? 0)}</div>
            </div>
          ))}
        </>
      )}

      {/* Electricity */}
      {elec.length > 0 && (
        <>
          <div style={{ ...s.sectionTitle, marginTop: 10 }}>Điện — Nước</div>
          {elec.map((m, i) => (
            <div key={i} style={s.row}>
              <div style={s.rowLabel}>
                ⚡ Tiền điện
                <div style={s.rowSub}>
                  {(m.previous_reading ?? 0).toLocaleString('vi-VN')} →{' '}
                  {(m.adjusted_reading ?? m.reading_value ?? 0).toLocaleString('vi-VN')} ={' '}
                  {(m.usage_amount ?? 0).toLocaleString('vi-VN')} kWh × {VND(m.unit_price ?? 0)}
                </div>
              </div>
              <div style={s.rowAmt}>{VND(m.total_cost ?? 0)}</div>
            </div>
          ))}
          {water.map((m, i) => (
            <div key={i} style={s.row}>
              <div style={s.rowLabel}>
                💧 Tiền nước
                <div style={s.rowSub}>
                  {(m.previous_reading ?? 0).toLocaleString('vi-VN')} →{' '}
                  {(m.adjusted_reading ?? m.reading_value ?? 0).toLocaleString('vi-VN')} ={' '}
                  {(m.usage_amount ?? 0).toLocaleString('vi-VN')} m³ × {VND(m.unit_price ?? 0)}
                </div>
              </div>
              <div style={s.rowAmt}>{VND(m.total_cost ?? 0)}</div>
            </div>
          ))}
        </>
      )}

      {/* Surcharges */}
      {surcharges.length > 0 && (
        <>
          <div style={{ ...s.sectionTitle, marginTop: 10 }}>Phụ phí</div>
          {surcharges.map((s2, i) => (
            <div key={i} style={s.row}>
              <div style={s.rowLabel}>{s2.label || s2.description || 'Phụ phí'}</div>
              <div style={s.rowAmt}>{VND(s2.total ?? s2.amount ?? 0)}</div>
            </div>
          ))}
        </>
      )}

      {/* Discounts */}
      {discounts.length > 0 && discounts.map(([key, svc]) => {
        const amt = parseFloat(svc?.price ?? svc?.amount ?? 0);
        if (!amt) return null;
        return (
          <div key={key} style={{ ...s.row, color: '#16a34a' }}>
            <div style={s.rowLabel}>{svc?.name || (key === 'first_month_discount' ? 'Giảm giá tháng đầu' : 'Hoa hồng')}</div>
            <div style={{ ...s.rowAmt, color: '#16a34a' }}>−{VND(Math.abs(amt))}</div>
          </div>
        );
      })}

      {/* Total */}
      <div style={s.divider} />
      <div style={s.totalRow}>
        <span>Tổng cộng</span>
        <span>{VND(totalInvoice)}</span>
      </div>
      <div style={{ ...s.row, borderBottom: 'none', paddingTop: 2 }}>
        <span style={{ fontSize: 12, color: '#78716c' }}>Đã thanh toán</span>
        <span style={{ fontSize: 12, fontWeight: 600, color: '#16a34a' }}>{VND(payment.paid_amount ?? 0)}</span>
      </div>
      {outstanding > 0 && (
        <div style={{ ...s.row, borderBottom: 'none', paddingTop: 2 }}>
          <span style={{ fontSize: 12, color: '#78716c' }}>Còn lại</span>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#ef4444' }}>{VND(outstanding)}</span>
        </div>
      )}

      {/* Proration note */}
      {isProrated && (
        <div style={s.prorateBox}>
          ✂ Hóa đơn tính tỷ lệ {prorateMatch[1]}/{prorateMatch[2]} ngày thực ở
          {moveoutMatch ? ` (ngày rời: ${moveoutMatch[1].split('-').reverse().join('/')})` : ''}
        </div>
      )}

      {/* Footer */}
      <div style={s.footer}>
        Xuất ngày {generatedAt} · Hệ thống quản lý nhà trọ
      </div>
    </div>
  );
}
